import React from "react";

import { IgSelect, IgTextField, IgTypography } from "@intelligems/ig-theme";
import { OnboardingInfoFe } from "@intelligems/ig-types";
import { MenuItem, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { getMixpanelStepTitle } from "@src/components/MainApp/Onboarding/utils";
import { useViewPage } from "@src/hooks/mixpanel";

const buyingIntentions: string[] = [
  "Just started looking for a testing/personalization tool",
  "Looking to upgrade from a current tool",
  "Actively evaluating several solutions for our needs",
  "Curious to explore what Intelligems can do for our business",
  "Specific feature or capability caught our attention",
  "Looking to solve a specific business challenge",
  "Other",
];
const sourceAttributions: string[] = [
  "Used Intelligems in the past",
  "Social media (LinkedIn, Twitter, etc)",
  "Blog post or article",
  "Podcast or Youtube video",
  "Conference or event",
  "Email newsletter",
  "Recommended by agency or consultant",
  "Recommended by another merchant",
  "Shopify expert or partner",
  "ChatGPT / LLM",
  "Google / Shopify app store search",
  "Advertisement",
  "Other",
];

interface OnboardingSalesInfoProps {
  data: Pick<
    OnboardingInfoFe,
    | "buyingIntention"
    | "otherBuyingIntention"
    | "sourceAttribution"
    | "otherSourceAttribution"
  >;
  onChange: (field: keyof OnboardingInfoFe, value: string) => void;
}

export const OnboardingSalesInfo = ({
  onChange,
  data,
}: OnboardingSalesInfoProps) => {
  const theme = useTheme();
  useViewPage(`Onboarding - ${getMixpanelStepTitle(3)}`);
  return (
    <Stack
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor: "transparent !important",
        },
      }}
      direction={"column"}
      spacing={6}
      width={"100%"}
    >
      <Stack direction={"column"} spacing={2}>
        <IgTypography variant={"h1"} fontWeight={"600"}>
          Help us understand your needs
        </IgTypography>
        <IgTypography color={theme.palette.text.secondary}>
          Your answers will help us provide you with the most relevant resources
          and support during your trial.
        </IgTypography>
      </Stack>
      <Stack direction={"column"} spacing={4}>
        <IgSelect
          fullWidth={true}
          placeholder={"Select an option"}
          label="What prompted you to try Intelligems today?"
          value={data.buyingIntention ?? "ig-empty"}
          onChange={(e) => onChange("buyingIntention", e.target.value)}
          required
        >
          {buyingIntentions.map((buyingIntention, index) => (
            <MenuItem key={index} value={buyingIntention}>
              {buyingIntention}
            </MenuItem>
          ))}
        </IgSelect>
        {data.buyingIntention === "Other" && (
          <IgTextField
            fullWidth={true}
            label="Please specify what prompted you"
            placeholder="Tell us what brought you to Intelligems..."
            value={data.otherBuyingIntention || ""}
            onChange={(e) => onChange("otherBuyingIntention", e.target.value)}
            required
            slotProps={{
              htmlInput: {
                maxLength: 100,
              },
            }}
          />
        )}
        <IgSelect
          fullWidth={true}
          placeholder={"Select an option"}
          label="How did you hear about Intelligems?"
          value={data.sourceAttribution ?? "ig-empty"}
          onChange={(e) => onChange("sourceAttribution", e.target.value)}
          required
        >
          {sourceAttributions.map((sourceAttribution, index) => (
            <MenuItem key={index} value={sourceAttribution}>
              {sourceAttribution}
            </MenuItem>
          ))}
        </IgSelect>
        {data.sourceAttribution === "Other" && (
          <IgTextField
            fullWidth={true}
            label="Please specify how you heard about us"
            placeholder="Tell us where you heard about Intelligems..."
            value={data.otherSourceAttribution || ""}
            onChange={(e) => onChange("otherSourceAttribution", e.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: 100,
              },
            }}
            required
          />
        )}
      </Stack>
    </Stack>
  );
};
